<!-- ─────────────────────────────────────────────────────────────────────── -->
<!-- Account Settings Dialog                                              -->
<!-- Four-tab dialog: Details, Status, Signature, Auto Reply              -->
<!-- Uses app-shared-dialog for the structural shell.                      -->
<!-- ─────────────────────────────────────────────────────────────────────── -->
<!-- ─────────────────────────────────────────────────────────────────────── -->
<!-- Account modal — Details / Signature / Auto Reply                       -->
<!-- Tabs only for the owner; see the component's class comment.            -->
<!-- Closes on backdrop click (the opener leaves `disableClose` at false).  -->
<!-- ─────────────────────────────────────────────────────────────────────── -->
<app-shared-dialog [options]="dialogOptions">

    <!-- ─── Header ───────────────────────────────────────────────────── -->
    <div class="account-dialog__header">
        <span class="account-dialog__header-icon">
            <ui-icon name="Mail" size="large"></ui-icon>
        </span>
        <div class="account-dialog__header-text">
            <span class="account-dialog__header-name">{{ account.name }}</span>
            <span class="account-dialog__header-sub">{{ account.email }}</span>
        </div>
        <ui-button
            class="account-dialog__close"
            type="transparent"
            lucideIcon="X"
            iconSize="medium"
            [ariaLabel]="'common.close' | translate"
            (click)="close()">
        </ui-button>
    </div>

    <!-- ─── Tab bar ──────────────────────────────────────────────────── -->
    <nav class="account-dialog__tabs" *ngIf="showTabs">
        <button *ngFor="let tab of tabs; let i = index"
                type="button"
                class="account-dialog__tab"
                [class.account-dialog__tab--active]="activeTabIndex === i"
                (click)="activeTabIndex = i">
            {{ tab.label | translate }}
        </button>
    </nav>

    <!-- ─── Details ──────────────────────────────────────────────────── -->
    <div class="account-dialog__body" *ngIf="activeTabIndex === AccountSettingsTab.Details">

        <!-- Owner -->
        <section class="account-dialog__section">
            <h4 class="account-dialog__section-title">
                {{ 'smart_inbox.inbox_view.account_settings_dialog.owner' | translate }}
            </h4>
            <div class="account-dialog__owner">
                <span class="account-dialog__owner-avatar">{{ ownerInitials }}</span>
                <span class="account-dialog__owner-name">{{ ownerName }}</span>
            </div>
        </section>

        <!-- Display name — saves on confirm, no dialog-wide Save button -->
        <section class="account-dialog__section">
            <h4 class="account-dialog__section-title">
                {{ 'smart_inbox.inbox_view.account_settings_dialog.display_name' | translate }}
            </h4>

            <div class="account-dialog__name" *ngIf="!isEditingName" (click)="startEditingName()">
                <ui-icon name="Mail" size="medium"></ui-icon>
                <span class="account-dialog__name-text">{{ account.name }}</span>
                <ui-icon name="Pencil" size="small" class="account-dialog__name-edit"></ui-icon>
            </div>

            <div class="account-dialog__name-input" *ngIf="isEditingName">
                <ui-form-field
                    [(ngModel)]="editedName"
                    [fullWidth]="true"
                    [placeholder]="'smart_inbox.inbox_view.account_settings_dialog.enter_display_name' | translate">
                </ui-form-field>
                <ui-button
                    type="secondary"
                    lucideIcon="Check"
                    iconSize="medium"
                    [isLoading]="loadingState.name"
                    [disabled]="loadingState.name || !editedName.trim()"
                    (click)="confirmNameChange()">
                </ui-button>
            </div>
        </section>

        <!-- Connection status — folded in from the old Status tab -->
        <section class="account-dialog__section">
            <div class="account-dialog__section-head">
                <h4 class="account-dialog__section-title">
                    {{ 'smart_inbox.inbox_view.account_settings_dialog.connection_status' | translate }}
                </h4>
                <span class="account-dialog__status-badge" [class]="'account-dialog__status-badge is-' + connectionTone">
                    <span class="account-dialog__status-dot" [class.is-connected]="isConnected" aria-hidden="true"></span>
                    {{ ('smart_inbox.inbox_view.account_settings_dialog.connection_status_' + (account.connection_status || 'error')) | translate }}
                </span>
            </div>

            <!-- Says what a disconnect did and did not touch: the fear here is data loss. -->
            <div class="account-dialog__status-panel" *ngIf="isDisconnected">
                <span class="account-dialog__status-panel-title">
                    {{ 'smart_inbox.inbox_view.account_settings_dialog.disconnected_title' | translate }}
                </span>
                <span class="account-dialog__status-panel-text">
                    {{ 'smart_inbox.inbox_view.account_settings_dialog.disconnected_body' | translate }}
                </span>
            </div>

            <dl class="account-dialog__stats">
                <!-- Provider logo leads the row. Its name stays available on hover, so
                     the logo is never the only carrier of the information. -->
                <ui-svg-icon *ngIf="providerIcon"
                             class="account-dialog__stats-provider"
                             [svgIcon]="providerIcon"
                             size="large"
                             [matTooltip]="providerLabel"
                             matTooltipClass="conversation-tooltip-info">
                </ui-svg-icon>
                <ui-icon *ngIf="!providerIcon"
                         class="account-dialog__stats-provider"
                         name="Mail"
                         size="large"
                         [matTooltip]="providerLabel"
                         matTooltipClass="conversation-tooltip-info">
                </ui-icon>

                <div class="account-dialog__stat">
                    <dt>{{ 'smart_inbox.inbox_view.account_settings_dialog.connected_since' | translate }}</dt>
                    <dd>{{ account.created_at ? (account.created_at | adaptToUserUtcOffset | date:'mediumDate') : '--' }}</dd>
                </div>
                <div class="account-dialog__stat">
                    <dt>{{ 'smart_inbox.inbox_view.account_settings_dialog.last_synced' | translate }}</dt>
                    <dd>{{ account.last_sync_at ? (account.last_sync_at | adaptToUserUtcOffset | date:'short') : '--' }}</dd>
                </div>
                <div class="account-dialog__stat">
                    <dt>{{ 'smart_inbox.inbox_view.account_settings_dialog.synced_emails' | translate }}</dt>
                    <dd>{{ account.total_threads !== null ? account.total_threads : '--' }}</dd>
                </div>
            </dl>
        </section>

        <!-- Account type -->
        <section class="account-dialog__section">
            <h4 class="account-dialog__section-title">
                {{ 'smart_inbox.inbox_view.account_settings_dialog.account_type' | translate }}
            </h4>

            <mat-radio-group class="account-dialog__types"
                             [class.account-dialog__types--readonly]="!canChangeType"
                             [(ngModel)]="selectedAccountType"
                             [disabled]="loadingState.type || !canChangeType">
                <label class="account-dialog__type"
                       [class.account-dialog__type--selected]="selectedAccountType === 'personal'"
                       (click)="canChangeType && !loadingState.type && selectedAccountType !== 'personal' && onTypeChange({ value: 'personal' })">
                    <mat-radio-button value="personal"></mat-radio-button>
                    <span class="account-dialog__type-text">
                        <span class="account-dialog__type-label">
                            {{ 'smart_inbox.inbox_view.account_settings_dialog.personal' | translate }}
                        </span>
                        <span class="account-dialog__type-desc">
                            {{ 'smart_inbox.inbox_view.account_settings_dialog.personal_description' | translate }}
                        </span>
                    </span>
                </label>

                <label class="account-dialog__type"
                       [class.account-dialog__type--selected]="selectedAccountType === 'team'"
                       (click)="canChangeType && !loadingState.type && selectedAccountType !== 'team' && onTypeChange({ value: 'team' })">
                    <mat-radio-button value="team"></mat-radio-button>
                    <span class="account-dialog__type-text">
                        <span class="account-dialog__type-label">
                            {{ 'smart_inbox.inbox_view.account_settings_dialog.team' | translate }}
                        </span>
                        <span class="account-dialog__type-desc">
                            {{ 'smart_inbox.inbox_view.account_settings_dialog.team_description' | translate }}
                        </span>
                    </span>
                </label>
            </mat-radio-group>
        </section>
    </div>

    <!-- ─── Signature Tab ────────────────────────────────────────────── -->
    <div class="account-dialog__body" *ngIf="activeTabIndex === AccountSettingsTab.Signature">

        <!-- Loading state -->
        <div class="account-dialog__signature-loading" *ngIf="signaturesLoading">
            <mat-spinner diameter="32"></mat-spinner>
            <span>{{ 'smart_inbox.inbox_view.account_settings_dialog.loading_signatures' | translate }}</span>
        </div>

        <ng-container *ngIf="!signaturesLoading">
            <!-- Signature list + preview -->
            <div class="account-dialog__signature-panel">
                <div class="account-dialog__signature-list">
                    <span class="account-dialog__label">
                        {{ 'smart_inbox.inbox_view.account_settings_dialog.signatures' | translate }}
                    </span>

                    <div class="account-dialog__signature-item"
                         *ngFor="let sig of signatures"
                         [class.account-dialog__signature-item--selected]="sig.id === selectedSignatureId"
                         [class.account-dialog__signature-item--editing]="!isDefaultSignature(sig) && renamingSignatureId === sig.id"
                         (click)="selectSignature(sig.id)">

                        <!-- Default signature (first): no rename/delete -->
                        <ng-container *ngIf="isDefaultSignature(sig)">
                            <span class="account-dialog__signature-name">{{ sig.name }}</span>
                        </ng-container>

                        <!-- Non-default: display mode -->
                        <ng-container *ngIf="!isDefaultSignature(sig) && renamingSignatureId !== sig.id">
                            <span class="account-dialog__signature-name">{{ sig.name }}</span>
                            <span class="account-dialog__signature-actions">
                                <ui-svg-icon
                                    svgIcon="pen-cc"
                                    size="small"
                                    (click)="startRenamingSignature(sig); $event.stopPropagation()">
                                </ui-svg-icon>
                                <ui-svg-icon
                                    svgIcon="trash-cc"
                                    size="small"
                                    (click)="removeSignature(sig); $event.stopPropagation()">
                                </ui-svg-icon>
                            </span>
                        </ng-container>

                        <!-- Non-default: inline rename mode -->
                        <div class="account-dialog__signature-rename"
                             *ngIf="!isDefaultSignature(sig) && renamingSignatureId === sig.id"
                             (click)="$event.stopPropagation()">
                            <ui-form-field
                                [(ngModel)]="editedSignatureName"
                                [fullWidth]="true"
                                [placeholder]="'smart_inbox.inbox_view.account_settings_dialog.enter_signature_name' | translate">
                            </ui-form-field>
                            <ui-button
                                type="transparent"
                                icon="check-cc"
                                iconSize="medium"
                                [disabled]="!editedSignatureName.trim()"
                                (click)="confirmSignatureRename()">
                            </ui-button>
                        </div>
                    </div>

                    <div class="account-dialog__signature-add" (click)="addSignature()">
                        <span>+</span>
                        <span>{{ 'smart_inbox.inbox_view.account_settings_dialog.create_new' | translate }}</span>
                    </div>
                </div>

                <div class="account-dialog__signature-preview">
                    <span *ngIf="signaturePreviewPlaceholder" class="account-dialog__signature-placeholder">
                        {{ 'smart_inbox.inbox_view.account_settings_dialog.no_signature_content' | translate }}
                    </span>
                    <div *ngIf="!signaturePreviewPlaceholder" [innerHTML]="selectedSignatureHtml"></div>
                </div>
            </div>

            <!-- Edit mode toggles -->
            <div class="account-dialog__signature-mode">
                <button class="account-dialog__mode-btn"
                        [class.account-dialog__mode-btn--active]="signatureEditMode === 'html'"
                        (click)="setSignatureEditMode('html')">
                    <span class="account-dialog__mode-icon">&lt;&gt;</span>
                    {{ 'smart_inbox.inbox_view.account_settings_dialog.write_html' | translate }}
                </button>
                <button class="account-dialog__mode-btn"
                        [class.account-dialog__mode-btn--active]="signatureEditMode === 'upload'"
                        (click)="setSignatureEditMode('upload')">
                    <ui-svg-icon svgIcon="upload-icon" size="small"></ui-svg-icon>
                    {{ 'smart_inbox.inbox_view.account_settings_dialog.upload_html' | translate }}
                </button>
            </div>

            <!-- HTML editor -->
            <ng-container *ngIf="signatureEditMode === 'html'">
                <textarea
                    class="account-dialog__signature-editor"
                    [ngModel]="selectedSignature?.body || ''"
                    (ngModelChange)="updateSignatureContent($event)"
                    (blur)="saveSignatureBody()"
                    [placeholder]="'smart_inbox.inbox_view.account_settings_dialog.signature_placeholder' | translate">
                </textarea>
            </ng-container>

            <!-- File upload -->
            <ng-container *ngIf="signatureEditMode === 'upload'">
                <div class="account-dialog__signature-upload">
                    <input #signatureFileInput type="file" accept=".html,.htm" hidden (change)="onSignatureFileUpload($event)" />
                    <ui-svg-icon svgIcon="upload-icon" size="medium" class="account-dialog__upload-icon"></ui-svg-icon>
                    <span class="account-dialog__upload-text">
                        {{ 'smart_inbox.inbox_view.account_settings_dialog.upload_html_description' | translate }}
                    </span>
                    <ui-button
                        type="secondary"
                        [label]="'smart_inbox.inbox_view.account_settings_dialog.browse_files' | translate"
                        (click)="signatureFileInput.click()">
                    </ui-button>
                </div>
            </ng-container>

            <!-- Signature defaults -->
            <div class="account-dialog__signature-defaults">
                <span class="account-dialog__label">
                    {{ 'smart_inbox.inbox_view.account_settings_dialog.signature_defaults' | translate }}
                </span>

                <div class="account-dialog__defaults-row">
                    <div class="account-dialog__default-field">
                        <span class="account-dialog__default-label">
                            {{ 'smart_inbox.inbox_view.account_settings_dialog.for_new_emails' | translate }}
                        </span>
                        <mat-select [ngModel]="defaultNewId"
                                    (ngModelChange)="onDefaultNewChange($event)"
                                    [placeholder]="'smart_inbox.inbox_view.account_settings_dialog.no_signature' | translate">
                            <mat-option [value]="null">
                                {{ 'smart_inbox.inbox_view.account_settings_dialog.no_signature' | translate }}
                            </mat-option>
                            <mat-option *ngFor="let sig of signatures" [value]="sig.id">
                                {{ sig.name }}
                            </mat-option>
                        </mat-select>
                    </div>

                    <div class="account-dialog__default-field">
                        <span class="account-dialog__default-label">
                            {{ 'smart_inbox.inbox_view.account_settings_dialog.on_reply_forward' | translate }}
                        </span>
                        <mat-select [ngModel]="defaultReplyForwardId"
                                    (ngModelChange)="onDefaultReplyForwardChange($event)"
                                    [placeholder]="'smart_inbox.inbox_view.account_settings_dialog.no_signature' | translate">
                            <mat-option [value]="null">
                                {{ 'smart_inbox.inbox_view.account_settings_dialog.no_signature' | translate }}
                            </mat-option>
                            <mat-option *ngFor="let sig of signatures" [value]="sig.id">
                                {{ sig.name }}
                            </mat-option>
                        </mat-select>
                    </div>
                </div>
            </div>
        </ng-container>
    </div>

    <!-- ─── Auto Reply Tab ───────────────────────────────────────────── -->
    <!-- Commented out with its entry in `tabs`: a lone toggle with nothing behind
         it yet. Re-enable both together.
    <div class="account-dialog__body" *ngIf="activeTabIndex === AccountSettingsTab.AutoReply">
        <div class="account-dialog__auto-reply">
            <span class="account-dialog__label">
                {{ 'smart_inbox.inbox_view.account_settings_dialog.auto_reply' | translate }}
            </span>
            <mat-slide-toggle [(ngModel)]="autoReplySettings.enabled"></mat-slide-toggle>
        </div>
    </div>
    -->

    <!-- ─── Sticky footer ────────────────────────────────────────────── -->
    <!-- Connected: refresh or step away. Disconnected: bring it back or remove it.
         Never both sets, so the destructive action is never adjacent to the one
         that undoes it. -->
    <!-- Details only: these act on the connection, which is what Details is about.
         On Signature they would sit under unrelated content. -->
    <div class="account-dialog__footer" *ngIf="activeTabIndex === AccountSettingsTab.Details">
        <ng-container *ngIf="!isDisconnected">
            <ui-button
                type="secondary"
                lucideIcon="RefreshCw"
                iconSize="medium"
                [label]="'smart_inbox.inbox_view.account_settings_dialog.refresh_connection' | translate"
                [isLoading]="loadingState.refresh"
                [disabled]="loadingState.refresh"
                (click)="onRefresh()">
            </ui-button>
            <ui-button
                type="danger-flat"
                lucideIcon="Unlink"
                iconSize="medium"
                [label]="'smart_inbox.inbox_view.account_settings_dialog.disconnect' | translate"
                [isLoading]="loadingState.disconnect || loadingState.delete"
                (click)="onDisconnect()">
            </ui-button>
        </ng-container>

        <ng-container *ngIf="isDisconnected">
            <ui-button
                type="danger-flat"
                lucideIcon="Trash2"
                iconSize="medium"
                [label]="'smart_inbox.inbox_view.account_settings_dialog.delete_connection' | translate"
                [isLoading]="loadingState.delete"
                (click)="onDisconnect()">
            </ui-button>
            <ui-button
                *ngIf="canReconnect"
                type="primary"
                lucideIcon="Unplug"
                iconSize="medium"
                [label]="'smart_inbox.inbox_view.account_settings_dialog.reconnect' | translate"
                [isLoading]="loadingState.reconnect"
                [disabled]="loadingState.reconnect"
                (click)="onReconnect()">
            </ui-button>
        </ng-container>
    </div>
</app-shared-dialog>
